/**
 * Parámetros de la MEDICIÓN de modelos IFC (visor de planos de OPUS ci, `apps/web/src/lib/medicionIfc.ts`).
 *
 * Son umbrales, así que NO viven fijos en el código: se editan en la calibración del motor
 * (`motor_calibracion.calibracion.umbrales`, panel staff de Calibración) y el visor los lee de la API.
 * Esto es solo el DEFAULT del seed y el respaldo si la calibración no responde o trae un valor inválido.
 * Calibrados contra AC20-FZK-Haus (ArchiCAD), comparando contra las cantidades que el propio modelo
 * declara e IfcOpenShell (ver las ayudas del panel, migración 142).
 */
export interface ParametrosMedicionIfc {
  /**
   * Tolerancia (en veces) para decir que un ÁREA declarada mide lo mismo que la geometría. Separa la
   * cara de un muro (10,4 m²) de su huella (1,0 m²); admite que en ventanas lo declarado sea el vano y
   * la malla incluya el marco (vano 1,0 vs malla 1,30).
   */
  ifc_ventana_area: number;
  /** Idem para el LARGO: separa el largo de una losa (11,8 m) del perímetro de su vano (15,9 m). */
  ifc_ventana_longitud: number;
  /** Idem para el VOLUMEN: neto vs bruto con vanos difieren ~15%. */
  ifc_ventana_volumen: number;
  /**
   * Fracción mínima del área total que debe tener la cara mayor para tratar la pieza como de caras
   * planas; por debajo es curva y su cara se toma como la mitad del área total.
   */
  ifc_cobertura_cara_plana: number;
  /**
   * Idem para el PESO: un peso declarado (Qto NetWeight/GrossWeight) se usa si cuadra con
   * volumen × densidad dentro de esta tolerancia.
   */
  ifc_ventana_peso: number;
  /**
   * DENSIDAD (kg/m³) por FAMILIA de material, para pesar una pieza cuando el modelo no declara ni su
   * peso ni la densidad de su material. La familia sale del estándar (IFC4 `IfcMaterial.Category`), del
   * perfil IFC (I, L, U, T, C, Z, tubo: perfiles de acero) o, si el modelo no dice nada de eso, de lo
   * que la IA reconoce en el NOMBRE del material (una consulta por modelo, ver materialIfcDeducido.ts;
   * el peso así sacado queda marcado como estimado). Si no se sabe la familia, la pieza queda sin pesar.
   */
  ifc_densidad_acero: number;
  ifc_densidad_concreto: number;
  ifc_densidad_madera: number;
  ifc_densidad_aluminio: number;
  /** Agregadas 2026-10-01 (migr 163), PROVISIONALES: las valida dirección. */
  ifc_densidad_vidrio: number;
  ifc_densidad_mamposteria: number;
  ifc_densidad_yeso: number;
  /**
   * Concreto LIGERO (agregado ligero, celular, aireado), agregado 2026-10-02 (migr 168): un muro de
   * «Leichtbeton» pesado como concreto normal salía 50 % más pesado. Lo fijó el ingeniero a cargo.
   */
  ifc_densidad_concreto_ligero: number;
  /**
   * FORMA de la pieza, para decidir por qué magnitud se mide un tipo (la "medida que manda"): se mide
   * por LARGO si su largo es al menos estas veces su ancho (columna, viga, tubo, barandal).
   */
  ifc_forma_lineal_min: number;
  /**
   * Tamaño del archivo IFC (MB) a partir del cual se AVISA antes de abrirlo en el navegador: un modelo
   * así puede tardar minutos o dejar la pestaña sin memoria. No impide abrirlo; el usuario decide.
   * PROVISIONAL (2026-10-02): 77 MB abrió bien y 178 MB congeló el navegador de prueba.
   */
  ifc_aviso_tamano_mb: number;
  /**
   * Y por ÁREA si, sin ser lineal, su ancho es al menos estas veces su espesor (muro, losa, vidrio). Lo
   * que no es ni lineal ni plano se mide por VOLUMEN (zapata, dado, relleno); si además es un equipo con
   * conexiones de instalación (puertos), por pieza.
   */
  ifc_forma_plana_min: number;
  /**
   * NIVELES DEDUCIDOS (metros), para un modelo que no trae pisos (IfcBuildingStorey): las caras
   * superiores de las losas de piso (IfcSlab FLOOR/BASESLAB) que quedan a menos de esto entre sí son el
   * mismo nivel (una banqueta de 11 cm junto a la losa de planta baja), y una pieza cuya base queda a
   * menos de esto por debajo de un nivel es de ese nivel (un muro que arranca en el lecho bajo de la losa).
   */
  ifc_nivel_tolerancia: number;
}

export const PARAMETROS_MEDICION_IFC_DEFAULT: Readonly<ParametrosMedicionIfc> = {
  ifc_ventana_area: 1.7,
  ifc_ventana_longitud: 1.25,
  ifc_ventana_volumen: 1.25,
  ifc_cobertura_cara_plana: 0.1,
  ifc_ventana_peso: 1.25,
  ifc_densidad_acero: 7850,
  ifc_densidad_concreto: 2400,
  ifc_densidad_madera: 500,
  ifc_densidad_aluminio: 2700,
  // Provisionales (dirección): vidrio flotado ~2.500; mampostería (muro de block/tabique, volumen bruto
  // del muro) y yeso (tablaroca/aplanado) varían mucho con la pieza.
  ifc_densidad_vidrio: 2500,
  ifc_densidad_mamposteria: 1600,
  ifc_densidad_yeso: 1000,
  ifc_densidad_concreto_ligero: 1600,
  ifc_forma_lineal_min: 4,
  ifc_aviso_tamano_mb: 120,
  ifc_forma_plana_min: 6,
  ifc_nivel_tolerancia: 0.5,
};

/** Rango válido de cada parámetro (mismo criterio que la validación del panel). */
export const RANGO_PARAMETROS_MEDICION_IFC: Readonly<Record<keyof ParametrosMedicionIfc, readonly [number, number]>> = {
  ifc_ventana_area: [1, 10],
  ifc_ventana_longitud: [1, 10],
  ifc_ventana_volumen: [1, 10],
  ifc_cobertura_cara_plana: [0, 1],
  ifc_ventana_peso: [1, 10],
  ifc_densidad_acero: [7000, 8500],
  ifc_densidad_concreto: [1000, 3000],
  ifc_densidad_madera: [200, 1300],
  ifc_densidad_aluminio: [2500, 3000],
  ifc_densidad_vidrio: [2200, 2800],
  ifc_densidad_mamposteria: [500, 2600],
  ifc_densidad_yeso: [400, 1600],
  // del celular (~300) al estructural de agregado ligero (~2.000)
  ifc_densidad_concreto_ligero: [300, 2000],
  ifc_forma_lineal_min: [1.5, 50],
  ifc_aviso_tamano_mb: [10, 2000],
  ifc_forma_plana_min: [1.5, 100],
  ifc_nivel_tolerancia: [0.01, 2],
};

/** Familias de material que se saben pesar sin que el modelo declare su densidad. */
export type FamiliaMaterialIfc =
  | 'acero'
  | 'concreto'
  | 'concreto_ligero'
  | 'madera'
  | 'aluminio'
  | 'vidrio'
  | 'mamposteria'
  | 'yeso';

/** La densidad (kg/m³) de una familia según los parámetros de la calibración. */
export function densidadDeFamiliaIfc(p: ParametrosMedicionIfc, f: FamiliaMaterialIfc): number {
  switch (f) {
    case 'acero':
      return p.ifc_densidad_acero;
    case 'concreto':
      return p.ifc_densidad_concreto;
    case 'concreto_ligero':
      return p.ifc_densidad_concreto_ligero;
    case 'madera':
      return p.ifc_densidad_madera;
    case 'aluminio':
      return p.ifc_densidad_aluminio;
    case 'vidrio':
      return p.ifc_densidad_vidrio;
    case 'mamposteria':
      return p.ifc_densidad_mamposteria;
    case 'yeso':
      return p.ifc_densidad_yeso;
  }
}

/**
 * Completa y sanea lo que venga (calibración guardada, respuesta de la API): cada clave ausente, no
 * numérica o fuera de rango cae al default. Nunca tira.
 */
export function normalizarParametrosMedicionIfc(x: unknown): ParametrosMedicionIfc {
  const src = (x && typeof x === 'object' ? x : {}) as Record<string, unknown>;
  const out = { ...PARAMETROS_MEDICION_IFC_DEFAULT };
  for (const k of Object.keys(out) as (keyof ParametrosMedicionIfc)[]) {
    const v = src[k];
    const [mn, mx] = RANGO_PARAMETROS_MEDICION_IFC[k];
    if (typeof v === 'number' && Number.isFinite(v) && v >= mn && v <= mx) out[k] = v;
  }
  return out;
}
